import { PdfDestinationMode, PdfRotationAngle, PdfTextStyle } from './enumerator';
import { PdfDestination, _PdfDestinationHelper } from './pdf-page';
import { _PdfDictionary, _PdfName, _PdfReference } from './pdf-primitives';
import { _checkRotation, _getPageIndex, _isNullOrUndefined, _parseColor } from './utils';
/**
 * Represents a base class for all bookmark objects.
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Get bookmarks
 * let bookmarks: PdfBookmarkBase = document.bookmarks;
 * // Destroy the document
 * document.destroy();
 * ```
 */
export class PdfBookmarkBase {
    /**
     * Initializes a new instance of the `PdfBookmarkBase` class.
     *
     * @private
     * @param {_PdfDictionary} dictionary Outline dictionary.
     * @param {_PdfCrossReference} crossReference Cross reference.
     *
     */
    constructor(dictionary, crossReference) {
        this._bookMarkList = [];
        this._isExpanded = false;
        this._isLoadedBookmark = false;
        this._dictionary = dictionary;
        this._crossReference = crossReference;
    }
    /**
     * Gets the bookmark count (Read only).
     *
     * @returns {number} Number of bookmarks.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Get bookmark count
     * let bookmarkCount: number = bookmarks.count;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get count() {
        if (this._isLoadedBookmark && this._bookMarkList.length === 0) {
            this._reproduceTree();
        }
        return this._bookMarkList.length;
    }
    /**
     * Gets the boolean flag indicating whether the bookmark is expanded or not.
     *
     * @returns {boolean} whether the bookmark is expanded or not.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the boolean flag indicating whether the bookmark is expanded or not
     * let isExpanded: boolean = bookmark.isExpanded;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get isExpanded() {
        if (this._dictionary && this._dictionary.has('Count')) {
            return (this._dictionary.get('Count') >= 0);
        }
        else {
            return this._isExpanded;
        }
    }
    /**
     * Sets the boolean flag indicating whether the bookmark is expanded or not.
     *
     * @param {boolean} value whether the bookmark is expanded or not.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Sets the boolean flag indicating whether the bookmark is expanded or not
     * bookmark.isExpanded = true;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set isExpanded(value) {
        this._isExpanded = value;
        if (this.count > 0 && this._dictionary) {
            this._dictionary.update('Count', value ? this._bookMarkList.length : (-this._bookMarkList.length));
        }
    }
    /**
     * Gets the `PdfBookmark` at the specified index.
     *
     * @param {number} index Bookmark index.
     * @returns {PdfBookmark} Bookmark at the specified index.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Get bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    at(index) {
        let bookmark;
        if (index < 0 || index >= this.count) {
            throw Error('Index out of range.');
        }
        if (_isNullOrUndefined(this._bookMarkList) && this._bookMarkList.length > 0 && index < this._bookMarkList.length) {
            bookmark = this._bookMarkList[index];
        }
        return bookmark;
    }
    /**
     * Gets the boolean flag indicating whether `PdfBookmark` is present or not.
     *
     * @param {PdfBookmark} outline Bookmark.
     * @returns {boolean} whether the bookmark is present or not.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Get the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the boolean flag indicating whether `PdfBookmark` is present or not.
     * let isPresent: boolean = bookmarks.contains(bookmark);
     * // Destroy the document
     * document.destroy();
     * ```
     */
    contains(outline) {
        return this._bookMarkList.indexOf(outline) !== -1;
    }
    add(title, index) {
        let bookmark;
        if (this._dictionary) {
            const dictionary = new _PdfDictionary(this._crossReference);
            dictionary.update('Parent', this._reference);
            const reference = this._crossReference._getNextReference();
            this._crossReference._cacheMap.set(reference, dictionary);
            bookmark = new PdfBookmark(dictionary, this._crossReference);
            bookmark._reference = reference;
            bookmark.title = title;
            if (typeof index === 'undefined') {
                if (this.count === 0) {
                    this._dictionary.update('First', reference);
                    this._dictionary.update('Last', reference);
                }
                else {
                    const last = this.at(this.count - 1);
                    this._dictionary.update('Last', reference);
                    if (last && last._reference) {
                        dictionary.update('Prev', last._reference);
                        last._dictionary.update('Next', reference);
                    }
                }
                this._bookMarkList.push(bookmark);
            }
            else {
                if (index < 0 || index > this.count) {
                    throw new Error('Index out of range');
                }
                if (this.count === 0) {
                    this._dictionary.update('First', reference);
                    this._dictionary.update('Last', reference);
                    this._bookMarkList.push(bookmark);
                }
                else if (index === this.count) {
                    const last = this.at(this.count - 1);
                    this._dictionary.update('Last', reference);
                    if (last && last._reference) {
                        dictionary.update('Prev', last._reference);
                        last._dictionary.update('Next', reference);
                    }
                    this._bookMarkList.push(bookmark);
                }
                else if (index === 0) {
                    const first = this.at(0);
                    this._dictionary.update('First', reference);
                    if (first && first._reference) {
                        dictionary.update('Next', first._reference);
                        first._dictionary.update('Prev', reference);
                    }
                    this._updateBookmarkList(index, bookmark);
                }
                else {
                    const next = this.at(index);
                    const prev = this.at(index - 1);
                    if (prev && prev._reference && next && next._reference) {
                        dictionary.update('Prev', prev._reference);
                        prev._dictionary.update('Next', reference);
                        next._dictionary.update('Prev', reference);
                        dictionary.update('Next', next._reference);
                    }
                    this._updateBookmarkList(index, bookmark);
                }
            }
            this._updateCount();
        }
        return bookmark;
    }
    remove(value) {
        if (typeof value === 'string') {
            for (let i = this._bookMarkList.length - 1; i >= 0; i--) {
                const bookmark = this.at(i);
                if (bookmark.title === value) {
                    this.remove(i);
                }
                else if (bookmark.count > 0) {
                    bookmark.remove(value);
                }
            }
        }
        else {
            if (value >= 0 && value < this.count) {
                if (this.count === 1) {
                    this._removeFirst(this._dictionary);
                    this._removeLast(this._dictionary);
                    this._removeCount(this._dictionary);
                    this._bookMarkList = [];
                }
                else {
                    if (value === 0) {
                        const next = this.at(value + 1);
                        if (this._dictionary && next && next._reference) {
                            this._removePrevious(next._dictionary);
                            this._dictionary.update('First', next._reference);
                        }
                    }
                    else if (value === this.count - 1) {
                        const prev = this.at(value - 1);
                        if (this._dictionary && prev && prev._reference) {
                            this._removeNext(prev._dictionary);
                            this._dictionary.update('Last', prev._reference);
                        }
                    }
                    else {
                        const prev = this.at(value - 1);
                        const next = this.at(value + 1);
                        if (prev && prev._reference && next && next._reference) {
                            prev._dictionary.update('Next', next._reference);
                            next._dictionary.update('Prev', prev._reference);
                        }
                    }
                    this._updateBookmarkList(value);
                    if (this._dictionary) {
                        this._updateCount();
                    }
                }
            }
        }
    }
    /**
     * Removes all the bookmark from the collection.
     *
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Remove all the bookmark from the collection.
     * bookmarks.clear();
     * // Get count after removal of all outlines.
     * let count: number = bookmarks.count;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    clear() {
        this._removeFirst(this._dictionary);
        this._removeLast(this._dictionary);
        this._removeCount(this._dictionary);
        this._bookMarkList = [];
    }
    _removeFirst(dictionary) {
        if (dictionary && dictionary.has('First')) {
            delete dictionary._map.First;
            dictionary._updated = true;
        }
    }
    _removeLast(dictionary) {
        if (dictionary && dictionary.has('Last')) {
            delete dictionary._map.Last;
            dictionary._updated = true;
        }
    }
    _removeNext(dictionary) {
        if (dictionary && dictionary.has('Next')) {
            delete dictionary._map.Next;
            dictionary._updated = true;
        }
    }
    _removePrevious(dictionary) {
        if (dictionary && dictionary.has('Prev')) {
            delete dictionary._map.Prev;
            dictionary._updated = true;
        }
    }
    _removeCount(dictionary) {
        if (dictionary && dictionary.has('Count')) {
            delete dictionary._map.Count;
            dictionary._updated = true;
        }
    }
    _updateBookmarkList(index, bookmark) {
        const updatedList = [];
        let i = 0;
        if (typeof bookmark === 'undefined') {
            this._bookMarkList.forEach((entry) => {
                if (i !== index) {
                    updatedList.push(entry);
                }
                else {
                    const reference = entry._reference;
                    if (reference && this._crossReference._cacheMap.has(reference)) {
                        this._crossReference._cacheMap.get(reference)._updated = false;
                    }
                }
                i++;
            });
        }
        else {
            this._bookMarkList.forEach((entry) => {
                if (i === index) {
                    updatedList.push(bookmark);
                }
                updatedList.push(entry);
                i++;
            });
        }
        this._bookMarkList = updatedList;
    }
    _updateCount() {
        if (this.isExpanded || !this._dictionary.has('Count')) {
            this._dictionary.update('Count', this._bookMarkList.length);
        }
        else {
            this._dictionary.update('Count', -this._bookMarkList.length);
        }
    }
    _reproduceTree() {
        let firstBookmark = this._getBookmark(this);
        let isBookmark = (firstBookmark) ? true : false;
        while (isBookmark && firstBookmark._dictionary) {
            this._bookMarkList.push(firstBookmark);
            firstBookmark = firstBookmark._next;
            isBookmark = (firstBookmark) ? true : false;
        }
    }
    _getBookmark(bookmarkBase, isFirst = true) {
        const bookmarkBaseDictionary = bookmarkBase._dictionary;
        let bookMark;
        if (bookmarkBaseDictionary && bookmarkBaseDictionary.has(isFirst ? 'First' : 'Last')) {
            const reference = bookmarkBaseDictionary._get(isFirst ? 'First' : 'Last');
            if (_isNullOrUndefined(reference)) {
                const bookMarkDictionary = this._crossReference._fetch(reference);
                if (bookMarkDictionary) {
                    bookMark = new PdfBookmark(bookMarkDictionary, this._crossReference);
                    bookMark._reference = reference;
                }
            }
        }
        return bookMark;
    }
}
/**
 * Represents a bookmark in a PDF document
 *
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Get the bookmarks
 * let bookmarks: PdfBookmarkBase = document.bookmarks;
 * // Gets the bookmark at the specified index
 * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
 * // Destroy the document
 * document.destroy();
 * ```
 */
export class PdfBookmark extends PdfBookmarkBase {
    /**
     * Initializes a new instance of the `PdfBookmark` class.
     *
     * @private
     * @param {_PdfDictionary} dictionary Bookmark dictionary.
     * @param {_PdfCrossReference} crossReference Cross reference.
     *
     */
    constructor(dictionary, crossReference) {
        super(dictionary, crossReference);
        if (this._dictionary && !this._dictionary.has('Dest') && this._dictionary.has('A')) {
            const actionDictionary = this._dictionary.get('A');
            if (actionDictionary && actionDictionary.has('D')) {
                const destinationArray = actionDictionary.getRaw('D'); // eslint-disable-line
                this._dictionary.update('Dest', destinationArray);
            }
        }
        this._isLoadedBookmark = true;
    }
    /**
     * Gets the destination.
     *
     * @returns {PdfDestination} Page destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the destination
     * let destination: PdfDestination = bookmark.destination;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get destination() {
        if (!this._destination) {
            const namedDestination = this._obtainNamedDestination();
            if (namedDestination === null || typeof namedDestination === 'undefined') {
                const destinationHelper = new _PdfDestinationHelper(this._dictionary, 'Dest');
                this._destination = destinationHelper._obtainDestination();
            }
        }
        return this._destination;
    }
    /**
     * Sets the destination.
     *
     * @param {PdfDestination} value destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Set the destination
     * bookmark.destination = new PdfDestination(page, [100, 200]);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set destination(value) {
        if (value) {
            value._parent = this;
            this._destination = value;
            this._destination._initializePrimitive();
        }
    }
    /**
     * Gets the named destination.
     *
     * @returns {PdfNamedDestination} Named destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get namedDestination() {
        if (this._namedDestination === null || typeof this._namedDestination === 'undefined') {
            this._namedDestination = this._obtainNamedDestination();
        }
        return this._namedDestination;
    }
    /**
     * Sets the named destination.
     *
     * @param {PdfNamedDestination} value Named destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set namedDestination(value) {
        if (this._namedDestination !== value && this._dictionary) {
            this._namedDestination = value;
            const dictionary = new _PdfDictionary(this._crossReference);
            dictionary.update('D', value.title);
            dictionary.update('S', _PdfName.get('GoTo'));
            const reference = this._crossReference._getNextReference();
            this._crossReference._cacheMap.set(reference, dictionary);
            this._dictionary.update('A', reference);
        }
    }
    /**
     * Gets the bookmark title.
     *
     * @returns {string} Bookmark title.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the bookmark title
     * let bookmarkTitle: string = bookmark.title;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get title() {
        if (this._title === null || typeof this._title === 'undefined') {
            if (this._dictionary && this._dictionary.has('Title')) {
                this._title = this._dictionary.get('Title');
            }
            else {
                this._title = '';
            }
        }
        return this._title;
    }
    /**
     * Sets the bookmark title.
     *
     * @param {string} value Bookmark title.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Sets the bookmark title
     * bookmark.title = 'Syncfusion';
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set title(value) {
        this._title = value;
        if (this._dictionary) {
            this._dictionary.update('Title', value);
        }
    }
    /**
     * Gets the bookmark color.
     *
     * @returns {number[]} Bookmark color.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the bookmark color
     * let color: number[] = bookmark.color;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get color() {
        if (this._color === null || typeof this._color === 'undefined') {
            if (this._dictionary && this._dictionary.has('C')) {
                this._color = _parseColor(this._dictionary.getArray('C'));
            }
        }
        return (this._color) ? this._color : [0, 0, 0];
    }
    /**
     * Sets the bookmark color.
     *
     * @param {number[]} value Bookmark color.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Sets the bookmark color
     * bookmark.color = [255, 0, 0];
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set color(value) {
        this._color = value;
        if (this._dictionary) {
            this._dictionary.update('C', [Number.parseFloat((value[0] / 255).toFixed(7)),
                Number.parseFloat((value[1] / 255).toFixed(7)),
                Number.parseFloat((value[2] / 255).toFixed(7))]);
        }
    }
    /**
     * Gets the text style.
     *
     * @returns {PdfTextStyle} Text style.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the textStyle
     * let textStyle: PdfTextStyle = bookmark.textStyle;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get textStyle() {
        if (this._textStyle === null || typeof this._textStyle === 'undefined') {
            this._textStyle = this._obtainTextStyle();
        }
        return this._textStyle;
    }
    /**
     * Sets the text style.
     *
     * @param {PdfTextStyle} value Text style.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Sets the textStyle
     * bookmark.textStyle = PdfTextStyle.italic;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set textStyle(value) {
        this._textStyle = value;
        this._updateTextStyle(value);
    }
    get _next() {
        let nextBookmark;
        if (this._dictionary && this._dictionary.has('Next')) {
            const reference = this._dictionary._get('Next');
            if (_isNullOrUndefined(reference)) {
                const dictionary = this._crossReference._fetch(reference);
                if (dictionary) {
                    nextBookmark = new PdfBookmark(dictionary, this._crossReference);
                    nextBookmark._reference = reference;
                }
            }
        }
        return nextBookmark;
    }
    _updateTextStyle(value) {
        if (value === PdfTextStyle.regular) {
            if (this._dictionary && this._dictionary.has('F')) {
                delete this._dictionary._map.F;
            }
        }
        else if (this._dictionary) {
            this._dictionary.update('F', value);
        }
    }
    _obtainTextStyle() {
        let style = PdfTextStyle.regular;
        if (this._dictionary && this._dictionary.has('F')) {
            const flag = this._dictionary.get('F');
            let flagValue = 0;
            if (typeof flag !== 'undefined' && flag !== null) {
                flagValue = flag;
            }
            style |= flagValue;
        }
        return style;
    }
    _obtainNamedDestination() {
        const document = this._crossReference._document;
        let destinationCollection;
        if (document) {
            destinationCollection = document._destinationCollection;
        }
        let destination; // eslint-disable-line
        let namedDestination;
        if (destinationCollection) {
            const dictionary = this._dictionary;
            if (dictionary) {
                if (dictionary.has('A')) {
                    const action = dictionary.get('A');
                    if (action && action.has('D')) {
                        destination = action.get('D');
                    }
                }
                else if (dictionary.has('Dest')) {
                    destination = dictionary.get('Dest');
                }
            }
            if (destination) {
                let value;
                if (destination instanceof _PdfName) {
                    value = destination.name;
                }
                else if (typeof destination === 'string') {
                    value = destination;
                }
                if (value) {
                    const namedDestinations = destinationCollection._namedDestinations;
                    if (namedDestinations && namedDestinations.length > 0) {
                        const entry = namedDestinations.find((dest) => dest._title === value);
                        if (entry) {
                            destination = entry;
                            namedDestination = entry;
                        }
                    }
                }
            }
        }
        return namedDestination;
    }
}
/**
 * Represents a named destination in a PDF document.
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Get the bookmarks
 * let bookmarks: PdfBookmarkBase = document.bookmarks;
 * // Gets the bookmark at the specified index
 * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
 * // Gets the named destination
 * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
 * // Destroy the document
 * document.destroy();
 * ```
 */
export class PdfNamedDestination {
    constructor(element, crossReference) {
        if (typeof element === 'string') {
            this._initialize();
            this.title = element;
        }
        else {
            this._dictionary = element;
            this._crossReference = crossReference;
        }
    }
    /**
     * Gets the destination.
     *
     * @returns {PdfDestination} Page destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Gets the destination
     * let destination: PdfDestination = namedDestination.destination;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get destination() {
        return this._destination;
    }
    /**
     * Sets the destination.
     *
     * @param {PdfDestination} value destination.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Set the destination
     * namedDestination.destination = new PdfDestination(page, [100, 200]);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set destination(value) {
        if (value) {
            value._parent = this;
            this._destination = value;
            this._destination._initializePrimitive();
        }
    }
    /**
     * Gets the title.
     *
     * @returns {string} title.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Gets the title
     * let title: string = namedDestination.title;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get title() {
        return this._title;
    }
    /**
     * Sets the title.
     *
     * @param {string} value title.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Get the bookmarks
     * let bookmarks: PdfBookmarkBase = document.bookmarks;
     * // Gets the bookmark at the specified index
     * let bookmark: PdfBookmark = bookmarks.at(0) as PdfBookmark;
     * // Gets the named destination
     * let namedDestination: PdfNamedDestination = bookmark.namedDestination;
     * // Set the title
     * namedDestination.title = 'Syncfusion';
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set title(value) {
        if (value !== this._title && this._dictionary) {
            const previousTitle = this._title;
            this._title = value;
            this._dictionary.update('Title', value);
            this._dictionary._updated = true;
            this._updateNamedDestinationTitle(value, previousTitle);
        }
    }
    _updateNamedDestinationTitle(value, previousTitle) {
        const crossReference = this._crossReference;
        if (crossReference && crossReference._document) {
            const document = crossReference._document;
            const catalogDictionary = document._catalog._catalogDictionary;
            if (catalogDictionary && catalogDictionary.has('Names')) {
                const names = catalogDictionary.get('Names');
                if (names && names.has('Dests')) {
                    const dests = names.get('Dests');
                    if (dests && dests.has('Names')) {
                        const namesValue = dests.get('Names'); // eslint-disable-line
                        const index = namesValue.indexOf(previousTitle);
                        if (index !== -1) {
                            namesValue.splice(index, 1, value);
                            delete dests._map.Names;
                            dests.update('Names', namesValue);
                            dests._updated = true;
                        }
                    }
                }
            }
        }
    }
    _initialize() {
        this._dictionary = new _PdfDictionary();
        this._dictionary.update('S', _PdfName.get('GoTo'));
    }
}
export class _PdfNamedDestinationCollection {
    constructor(dictionary, crossReference) {
        this._namedDestinations = [];
        if (dictionary) {
            this._dictionary = dictionary;
        }
        if (crossReference) {
            this._crossReference = crossReference;
        }
        if (dictionary && dictionary.has('Dests')) {
            const destination = dictionary.get('Dests');
            if (destination) {
                if (destination.has('Names')) {
                    this._addCollection(destination);
                }
                else if (destination.has('Kids')) {
                    const destinationArray = destination.getArray('Kids'); // eslint-disable-line
                    destinationArray.forEach((entry) => {
                        this._findDestination(entry);
                    });
                }
            }
        }
    }
    _findDestination(destination) {
        if (destination) {
            if (destination.has('Names')) {
                this._addCollection(destination);
            }
            else if (destination.has('Kids')) {
                const kids = destination.getArray('Kids'); // eslint-disable-line
                if (kids && Array.isArray(kids) && kids.length > 0) {
                    kids.forEach((entry) => {
                        this._findDestination(entry);
                    });
                }
            }
        }
    }
    _addCollection(destination) {
        let elements = destination.getRaw('Names'); // eslint-disable-line
        let ref; // eslint-disable-line
        let dictionary;
        if (elements && elements instanceof _PdfReference) {
            ref = this._crossReference._fetch(elements);
        }
        if (ref && Array.isArray(ref) && ref.length > 0) {
            elements = ref;
        }
        if (elements && Array.isArray(elements) && elements.length > 0) {
            for (let i = 1; i < elements.length; i = i + 2) {
                let reference = elements[i]; // eslint-disable-line
                if (reference && reference instanceof _PdfReference) {
                    const destinationArray = this._crossReference._fetch(reference); // eslint-disable-line
                    if (destinationArray && Array.isArray(destinationArray) && destinationArray.length > 0) {
                        dictionary = new _PdfDictionary();
                        dictionary.update('D', destinationArray);
                    }
                    else {
                        dictionary = this._crossReference._fetch(reference);
                    }
                }
                else if ((dictionary === null || typeof dictionary === 'undefined') && Array.isArray(reference)) {
                    dictionary = new _PdfDictionary();
                    dictionary.update('D', reference);
                }
                if (dictionary) {
                    const namedDestination = new PdfNamedDestination(dictionary, this._crossReference);
                    const value = elements[i - 1];
                    let destinationObject;
                    let destArray; // eslint-disable-line
                    if (value) {
                        namedDestination._title = value;
                        if (dictionary.has('D')) {
                            destArray = dictionary.get('D');
                            destinationObject = new PdfDestination();
                            let pageRef = destArray[0]; // eslint-disable-line
                            if (pageRef && destArray && destArray[0] instanceof _PdfReference) {
                                const pageDictionary = this._crossReference._fetch(pageRef);
                                const loadedDocument = this._crossReference._document;
                                let index;
                                if (loadedDocument && pageDictionary) {
                                    index = _getPageIndex(loadedDocument, pageDictionary);
                                    if (typeof index !== 'undefined' && index !== null && index >= 0) {
                                        destinationObject._index = index;
                                        destinationObject.page = loadedDocument.getPage(index);
                                    }
                                }
                            }
                        }
                    }
                    if (destArray[1] instanceof _PdfName) {
                        let left;
                        let height;
                        let zoom;
                        const mode = destArray[1].name;
                        const page = destinationObject.page;
                        switch (mode) {
                            case 'Fit':
                                destinationObject._destinationMode = PdfDestinationMode.fitToPage;
                                break;
                            case 'XYZ':
                                destinationObject._destinationMode = PdfDestinationMode.location;
                                if (destArray.length > 2) {
                                    left = destArray[2];
                                }
                                if (destArray.length > 3) {
                                    height = destArray[3];
                                }
                                if (destArray.length > 4) {
                                    zoom = destArray[4];
                                }
                                if (page) {
                                    const size = page.size;
                                    let topValue = (height === null || typeof height === 'undefined') ? 0 : size[1] - height;
                                    const leftValue = (left === null || typeof left === 'undefined') ? 0 : left;
                                    destinationObject._location = [leftValue, topValue];
                                    if (page.rotation !== PdfRotationAngle.angle0) {
                                        topValue = _checkRotation(page, height, left);
                                    }
                                    destinationObject._zoom = (typeof zoom !== 'undefined' && zoom !== null) ? zoom : 0;
                                    if (left === null || height === null || zoom === null || typeof left === 'undefined'
                                        || typeof height === 'undefined' || typeof zoom === 'undefined') {
                                        destinationObject._isValid = false;
                                    }
                                }
                                break;
                            case 'FitH':
                            case 'FitBH':
                                destinationObject._destinationMode = PdfDestinationMode.fitH;
                                if (destArray.length >= 3) {
                                    height = destArray[2];
                                }
                                if (page) {
                                    const size = page.size;
                                    const topValue = (height === null || typeof height === 'undefined') ? 0 : size[1] - height;
                                    destinationObject._location = [0, topValue];
                                }
                                if (height === null || typeof height === 'undefined') {
                                    destinationObject._isValid = false;
                                }
                                break;
                            case 'FitR':
                                destinationObject._destinationMode = PdfDestinationMode.fitR;
                                break;
                        }
                    }
                    destinationObject._parent = namedDestination;
                    namedDestination._destination = destinationObject;
                    this._namedDestinations.push(namedDestination);
                }
            }
        }
    }
}
